import React, { useEffect, useState, memo } from 'react';
import { Zap, BadgeCheck } from 'lucide-react';
import { paymentService } from '../../services/payment.service';
import { runInLane } from '../../utils/requestLane';
import ServiceWorkspaceLowerRails from './ServiceWorkspaceLowerRails';
import { formatMoney, getDefaultCurrency } from '../../utils/formatMoney';
import localeConfig from '../../config/locale.config';

const formatShortTs = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString(localeConfig.intlLocale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '—';
  }
};

const humanizeServiceStatus = (status) => {
  if (!status) return '—';
  return String(status).replace(/_/g, ' ');
};

const ServiceWorkspaceSidePanel = ({ serviceId, detailedServiceData, loadingDetailedService, paymentRefreshKey = 0 }) => {
  const [payment, setPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!serviceId) {
        setPayment(null);
        return;
      }
      setPaymentLoading(true);
      try {
        const response = await runInLane(() => paymentService.getServicePayment(serviceId));
        if (!cancelled) setPayment(response?.payment ?? null);
      } catch {
        if (!cancelled) setPayment(null);
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [serviceId, paymentRefreshKey]);

  if (!serviceId) {
    return (
      <div className="cwp-surface-card-elevated overflow-hidden p-5">
        <p className="text-sm leading-relaxed text-slate-600">
          Select a service from the list below to see live status, payment, and vendor for that service.
        </p>
      </div>
    );
  }

  if (loadingDetailedService || !detailedServiceData?.service) {
    return (
      <div className="space-y-4">
        <div className="cwp-surface-card-elevated overflow-hidden p-6">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          <p className="mt-3 text-center text-sm text-slate-500">Loading service summary…</p>
        </div>
      </div>
    );
  }

  const svc = detailedServiceData.service;
  const vl = detailedServiceData.vendor_location;
  const status = svc.status || '';
  const statusReadable = humanizeServiceStatus(status);

  let statusTone = 'bg-slate-100 text-slate-800 ring-slate-600/10';
  if (status === 'service_complete' || status === 'service_resolved') {
    statusTone = 'bg-emerald-50 text-emerald-800 ring-emerald-600/15';
  } else if (status === 'service_cancelled') {
    statusTone = 'bg-red-50 text-red-800 ring-red-600/15';
  } else if (status === 'pending' || status === 'awaiting_customer_payment') {
    statusTone = 'bg-amber-50 text-amber-900 ring-amber-600/20';
  } else {
    statusTone = 'bg-blue-50 text-blue-800 ring-blue-600/15';
  }

  const amountDisplay = () => {
    if (paymentLoading) return { mode: 'loading' };
    if (!payment) return { mode: 'empty', message: 'No payment recorded yet' };
    const code = (payment.currency || getDefaultCurrency()).toUpperCase();
    const primary = formatMoney(payment.amount, code);
    let sub = '';
    if (payment.status === 'succeeded') {
      sub = `${code} · paid`;
    } else if (payment.status) {
      sub = `${code} · ${payment.status}`;
    } else {
      sub = code;
    }
    return { mode: 'amount', primary, sub };
  };

  const resolvedBlurb = () => {
    const mins = svc.actual_time_minutes;
    if (mins != null && mins !== '' && !Number.isNaN(Number(mins))) {
      return `Resolved in ${mins} min`;
    }
    if (svc.started_at && svc.completed_at) {
      try {
        const m = Math.round((new Date(svc.completed_at) - new Date(svc.started_at)) / 60000);
        if (m > 0) return `Resolved in ${m} min`;
      } catch {
        /* ignore */
      }
    }
    return null;
  };

  const vendorName = vl?.vendor?.vendor_name || '—';
  const vendorCode = vl?.vendor?.vendor_code || vl?.vendor?.vendor_id || '—';
  const vendorAddr = vl?.location_address?.address || '—';
  const mgrName = vl?.manager?.name || '—';
  const mgrPhone = vl?.manager?.mobile || '—';
  const vendorInitial = (vendorName && vendorName.charAt(0).toUpperCase()) || '?';
  const paymentUi = amountDisplay();

  return (
    <div className="flex flex-col gap-4">
      {/* Live status */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-5 pb-3 pt-5">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Live status</span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${statusTone}`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" aria-hidden />
            {status === 'service_complete'
              ? 'Completed'
              : status === 'service_resolved'
                ? 'Service resolved'
                : statusReadable}
          </span>
        </div>
        <div className="px-5 pb-5">
          {paymentUi.mode === 'loading' ? (
            <p className="text-sm text-slate-500">Loading payment…</p>
          ) : paymentUi.mode === 'empty' ? (
            <p className="text-sm text-slate-600">{paymentUi.message}</p>
          ) : (
            <>
              <p className="text-2xl font-bold tracking-tight text-slate-900">{paymentUi.primary}</p>
              {paymentUi.sub ? <p className="mt-1 text-sm text-slate-500">{paymentUi.sub}</p> : null}
            </>
          )}
        </div>
        {resolvedBlurb() ? (
          <div className="border-t border-slate-100 px-5 py-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-700">
              <Zap className="h-4 w-4 shrink-0" aria-hidden />
              {resolvedBlurb()}
            </p>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3 border-t border-slate-100 px-5 py-4 text-xs">
          <div>
            <div className="font-semibold uppercase tracking-wide text-slate-500">Started</div>
            <div className="mt-1 text-sm font-medium leading-snug text-slate-900">{formatShortTs(svc.started_at)}</div>
          </div>
          <div>
            <div className="font-semibold uppercase tracking-wide text-slate-500">Completed</div>
            <div className="mt-1 text-sm font-medium leading-snug text-slate-900">{formatShortTs(svc.completed_at)}</div>
          </div>
        </div>
      </div>

      {/* Vendor */}
      <div className="cwp-surface-card-elevated relative overflow-hidden">
        <div className="flex items-start gap-2 border-b border-slate-100 px-5 py-4">
          <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden />
          <div>
            <h3 className="text-base font-semibold text-slate-900">Vendor</h3>
            <p className="text-xs text-slate-500">Assigned partner</p>
          </div>
        </div>

        <div className="px-5 py-4">
        {vl ? (
          <>
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
              <div className="flex gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-sm">
                  {vendorInitial}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{vendorName}</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-600">{vendorCode}</div>
                  <div className="mt-2 text-sm leading-snug text-slate-700">{vendorAddr}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-200/80 pt-4 text-xs">
              <div>
                <div className="font-semibold uppercase tracking-wide text-slate-500">Manager</div>
                <div className="mt-1 text-sm font-medium text-slate-900">{mgrName}</div>
              </div>
              <div>
                <div className="font-semibold uppercase tracking-wide text-slate-500">Contact</div>
                <div className="mt-1 text-sm font-medium text-slate-900">{mgrPhone}</div>
              </div>
            </div>
          </>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
            No vendor assigned for this service yet.
          </p>
        )}
        </div>
      </div>

      <ServiceWorkspaceLowerRails
        detailedServiceData={detailedServiceData}
        payment={payment}
        paymentLoading={paymentLoading}
      />
    </div>
  );
};

export default memo(ServiceWorkspaceSidePanel);
